import jsPDF from "jspdf";
import QRCode from "qrcode";
import { Deposit } from "../deposit-dtos";
import { getLocalisedName } from "../../../../utils/i18n";
import wfpLogoUrl from "../../../../assets/images/wfp-logo-emblem-black.png";

function loadImageAsDataUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas context unavailable"));
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = url;
  });
}

export async function generateTraceabilityPdf(
  deposit: Deposit,
  t: (key: string) => string
): Promise<void> {
  const depositUrl = `${window.location.origin}/representative/market/deposit/${deposit.uuid}`;
  const commodityName = getLocalisedName(
    deposit.farmer_produce?.commodity || deposit.commodity || "Unknown commodity"
  );

  const [qrDataUrl, logoPngDataUrl] = await Promise.all([
    QRCode.toDataURL(depositUrl, { width: 400, margin: 1 }),
    loadImageAsDataUrl(wfpLogoUrl),
  ]);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = 210;
  const pageH = 297;
  const halfW = pageW / 2;
  const halfH = pageH / 2;

  // Draw divider lines
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(halfW, 0, halfW, pageH); // vertical
  doc.line(0, halfH, pageW, halfH); // horizontal

  const offsets: [number, number][] = [
    [0, 0],
    [halfW, 0],
    [0, halfH],
    [halfW, halfH],
  ];

  const subtitle1 = t("farmersMarket.depositCard.traceabilitySubtitle1");
  const subtitle2 = t("farmersMarket.depositCard.traceabilitySubtitle2");

  for (const [ox, oy] of offsets) {
    drawCard(
      doc,
      ox,
      oy,
      halfW,
      halfH,
      commodityName,
      qrDataUrl,
      logoPngDataUrl,
      subtitle1,
      subtitle2
    );
  }

  const pdfBlob = doc.output("blob");
  const blobUrl = URL.createObjectURL(pdfBlob);
  window.open(blobUrl, "_blank");
}

function drawCard(
  doc: jsPDF,
  ox: number,
  oy: number,
  w: number,
  h: number,
  commodityName: string,
  qrDataUrl: string,
  logoDataUrl: string,
  subtitle1: string,
  subtitle2: string
): void {
  const cx = ox + w / 2;
  const margin = 12;

  // Commodity name
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(commodityName, ox + margin, oy + 20);

  // Subtitle
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(subtitle1, ox + margin, oy + 30);
  doc.text(subtitle2, ox + margin, oy + 35);

  // QR code — centered horizontally and vertically between subtitle and footer
  const qrSize = 50;
  const subtitleBottom = 37;
  const footerTop = h - 18;
  const qrY = oy + subtitleBottom + (footerTop - subtitleBottom - qrSize) / 2;
  doc.addImage(qrDataUrl, "PNG", cx - qrSize / 2, qrY, qrSize, qrSize);

  // Footer: WFP logo + Farm2Go text
  const logoSize = 10;
  const footerY = oy + h - 16;
  doc.addImage(logoDataUrl, "PNG", ox + margin, footerY - 2, logoSize, logoSize);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.text("Farm2Go", ox + w - margin - doc.getTextWidth("Farm2Go"), footerY + 5);
}

export default generateTraceabilityPdf;
